import React, { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { 
    BrowserRouter as Router, 
    Routes, 
    Route, 
    Navigate, 
    useLocation,
    useSearchParams,
    Link 
} from 'react-router-dom';
import './App.css';
import './styles/mobile-standard.css';
import api from './config/api';
import ScrollToTop from './components/ScrollToTop';
import { useNotification } from './components/NotificationManager';
import NavSettingsDropdown from './components/NavSettingsDropdown';
import CookieConsent, { hasAnalyticsConsent, COOKIE_CONSENT_EVENT } from './components/CookieConsent';
import LogoLoading from './components/LogoLoading';
import { buildListingLabel } from './utils/propertyTitle';
import { persistUserSession } from './utils/sessionUser';
import { loginPathForSession, logoutToLogin } from './utils/authExperience';
import { needsTermsAcceptance, sessionRequiresPasswordChange } from './utils/termsConsent';
import { homePathForUser, isPlatformAdminUser } from './utils/homePath';
import { SESSION_UPDATED_EVENT } from './brandKits';
import { getStoredUser, isProspectingHidden, isInventoryHidden, isStudioHidden, isNavHidden } from './utils/featureAccess';
import StudioTabRoute from './components/StudioTabRoute';
import { seesOfficeListingsTab } from './utils/seesAgencyListings';
import { isMembershipExperience, isMembershipAgencyOrAgent, isMembershipPartner, membershipAppHomePath, OPEN_ADD_LEAD_EVENT } from './utils/isMembershipExperience';
import { isAdashChromeUser, ADASH_BAR_HEIGHT, applyAdashRailMode, readAdashRailMode } from './utils/isAdashChrome';
import { prewarmDashboard } from './utils/dashboardPrewarm';
import { scheduleIdleRoutePrefetch } from './utils/prefetchAppRoutes';
import { SidebarProvider, useSidebar } from './context/SidebarContext';
import DemoModeBar, { getDemoState } from './components/DemoModeBar';
import WorkbenchPreviewSiteBanner from './components/WorkbenchPreviewSiteBanner';
import { PropdataImportProvider } from './context/PropdataImportContext';
import { LightstoneRetrievalProvider } from './context/LightstoneRetrievalContext';
import { CmaInfoRetrievalProvider } from './context/CmaInfoRetrievalContext';
import { useTranslation } from 'react-i18next';
import Home from './pages/Home';
import BrandLogo, { BrandWordmark } from './components/BrandLogo';
import { propertyNavStateFromLocation } from './utils/propertyNavigation';
import { PortalRoot, PortalAuthAlias } from './components/PortalEntry';
import { portalForHostname } from './utils/portalExperience';
import { PortalPublicBoundary, PortalPublicNotFound } from './components/PortalPublicBoundary';
import RuntimeIdentityBoundary from './components/RuntimeIdentityBoundary';

/** Routes that use DashboardSplitTopBar + sidebar shell (keep in sync - single source of truth). */
const DASHBOARD_LAYOUT_PATHS = [
    '/dashboard', '/portfolio', '/saved', '/vault', '/agency-dashboard', '/agent-dashboard',
    '/listing-management', '/office-listings', '/inventory', '/prospecting', '/agents', '/crm', '/crm/cma-report', '/sales', '/sales/deals',
    '/commission-engine', '/settings', '/add-listing', '/marketing', '/mass-marketing', '/portal-syndication',
    '/admin', '/admin/dashboard', '/admin/accounts', '/admin/demo-accounts', '/admin/demo-builder', '/admin/demo-websites', '/admin/listings', '/admin/screening', '/admin/referrals', '/admin/partners', '/admin/membership-verification', '/admin/bugs', '/admin/sprints', '/admin/workbench', '/admin/demo', '/admin/marketing', '/admin/mass-marketing', '/admin/cost-model', '/admin/scheduler', '/admin/ops', '/admin/template-portal', '/admin/ipm-sales',
    '/news', '/enterprise/agencies', '/enterprise/invites', '/enterprise/performance',
    '/enterprise/royalty-engine', '/enterprise/syndication', '/enterprise-dashboard',
    '/enterprise/performance-country', '/enterprise/performance-franchise', '/enterprise/performance-branch',
    '/enterprise/compliance-report', '/enterprise/portal-syndication', '/enterprise/marketing', '/enterprise/vault',
    '/partner-enterprise-dashboard', '/partner-enterprise/franchises', '/partner-enterprise/invites',
    '/bond-originator', '/bond-originator-dashboard', '/conveyancer', '/partner-dashboard',
    '/rental-dashboard', '/rental-applications', '/rental-pipeline', '/rental-payments', '/rental-maintenance', '/inspections/new', '/tenant-portal',
    '/investor-portfolio', '/managed-portfolio', '/clients', '/referrals', '/calendar', '/branch-planner',
    '/compliance/rcr', '/compliance/fica', '/my-bug-reports', '/my-reports', '/ipm-academy', '/membership/deals',
];

function isDashboardLayoutRoute(pathname, { authenticated = false } = {}) {
    const path = String(pathname || '').replace(/\/+$/, '') || '/';
    const isAuthenticatedPropertyView = path.startsWith('/property/') && authenticated;
    // New Developments is a public marketing page, but logged-in users should see
    // it inside the app shell (sidebar + dashboard top bar), not the public navbar.
    const isAuthenticatedNewDevelopments = path === '/new-developments' && authenticated;
    // Rental application is public (applicants open it via a shared link), but a
    // logged-in agent opening it should see it inside the app shell, not the
    // public navbar.
    const isAuthenticatedRentalApplication = path === '/rental-application' && authenticated;
    return DASHBOARD_LAYOUT_PATHS.includes(path)
        || path === '/admin' || path.startsWith('/admin/')
        || isAuthenticatedNewDevelopments
        || isAuthenticatedRentalApplication
        || path.startsWith('/clients/')
        || path.startsWith('/membership/deals')
        || path.startsWith('/my-reports')
        || path.startsWith('/brochures')
        || path.startsWith('/bond-originator')
        || path.startsWith('/conveyancer')
        || path.startsWith('/partner/')
        || path.startsWith('/enterprise/')
        || path.startsWith('/partner-enterprise/')
        || isAuthenticatedPropertyView;
}

// Marketing auth is huge (signup wizard). Keep it out of the landing JS.
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const SpeedInsights = lazy(() =>
    import('@vercel/speed-insights/react').then((m) => ({ default: m.SpeedInsights }))
);
const DashboardSplitTopBar = lazy(() => import('./components/DashboardSplitTopBar'));
const AdashAppBar = lazy(() => import('./components/AdashAppBar'));
const AdashSideNav = lazy(() => import('./components/AdashSideNav'));
const FloatingBugsFeedback = lazy(() => import('./components/FloatingBugsFeedback'));
const FloatingChat = lazy(() => import('./components/FloatingChat'));
const BugReviewBanner = lazy(() => import('./components/BugReviewBanner'));
const BugFixReviewModal = lazy(() => import('./components/BugFixReviewModal'));
const ForcedBugReviewPopup = lazy(() => import('./components/ForcedBugReviewPopup'));
const EnquiryDetailModal = lazy(() => import('./components/EnquiryDetailModal'));
const MembershipVerificationResultModal = lazy(() => import('./components/MembershipVerificationResultModal'));
const MembershipVerificationReviewHost = lazy(() => import('./components/MembershipVerificationReviewModal'));
const GuidedTour = lazy(() => import('./components/GuidedTour'));
const ExpertModals = lazy(() => import('./components/industryExpert/ExpertModals'));
const MobileFAB = lazy(() => import('./components/MobileFAB'));
const PayPropDrawer = lazy(() => import('./components/PayPropDrawer'));

// Lazy-loaded pages - only loaded when navigated to (Dashboard is heavy: recharts, maps, legacy)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AgencyRegistration = lazy(() => import('./pages/AgencyRegistration'));
const Collection = lazy(() => import('./pages/Collection'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const Property = lazy(() => import('./pages/Property'));
const Profile = lazy(() => import('./pages/Profile'));
const NewsDetail = lazy(() => import('./pages/NewsDetail'));
const NewDevelopments = lazy(() => import('./pages/NewDevelopments'));
const Saved = lazy(() => import('./pages/Saved'));
const IndependentAgentRegistration = lazy(() => import('./pages/IndependentAgentRegistration'));
const AgencyAgentInviteRegistration = lazy(() => import('./pages/AgencyAgentInviteRegistration'));
const AcceptAccountInvite = lazy(() => import('./pages/AcceptAccountInvite'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const PartnerRegistration = lazy(() => import('./pages/PartnerRegistration'));
const MyAds = lazy(() => import('./pages/MyAds'));
const About = lazy(() => import('./pages/About'));
const ClientRegistration = lazy(() => import('./pages/ClientRegistration'));
const ConsentPage = lazy(() => import('./pages/ConsentPage'));
const SignatureComplete = lazy(() => import('./pages/SignatureComplete'));
const ViewingBookingPage = lazy(() => import('./pages/ViewingBookingPage'));
const MeetBookingPage = lazy(() => import('./pages/MeetBookingPage'));
const ClientTracker = lazy(() => import('./pages/ClientTracker'));
const ClientFormFill = lazy(() => import('./pages/ClientFormFill'));
const Careers = lazy(() => import('./pages/Careers'));
const Legal = lazy(() => import('./pages/Legal'));
const Privacy = lazy(() => import('./pages/Privacy'));
const AddListing = lazy(() => import('./pages/AddListing'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Contact = lazy(() => import('./pages/Contact'));
const Vault = lazy(() => import('./pages/Vault'));
const Agents = lazy(() => import('./pages/Agents'));
const RentalDashboard = lazy(() => import('./pages/RentalDashboard'));
const RentalMaintenance = lazy(() => import('./pages/RentalMaintenance'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminCommunicationBilling = lazy(() => import('./pages/AdminCommunicationBilling'));
const AdminEmailHealth = lazy(() => import('./pages/AdminEmailHealth'));
const AdminUnitEconomics = lazy(() => import('./pages/AdminUnitEconomics'));
const AdminScheduler = lazy(() => import('./pages/AdminScheduler'));
const AdminOpsHealth = lazy(() => import('./pages/AdminOpsHealth'));
const AdminRuntimeIdentity = lazy(() => import('./pages/AdminRuntimeIdentity'));
const IpmSprintPlanner = lazy(() => import('./pages/IpmSprintPlanner'));
const AdminDemoBuilder = lazy(() => import('./pages/AdminDemoBuilder'));
const AdminScreening = lazy(() => import('./pages/AdminScreening'));
const FicaQueue = lazy(() => import('./pages/FicaQueue'));
const AdminReferrals = lazy(() => import('./pages/AdminReferrals'));
const AdminMassMarketing = lazy(() => import('./pages/AdminMassMarketing'));
const IpmSales = lazy(() => import('./pages/IpmSales'));
const AdminTemplatePortal = lazy(() => import('./pages/AdminTemplatePortal'));
const TemplatePreviewGallery = lazy(() => import('./pages/TemplatePreviewGallery'));
const AdminPartners = lazy(() => import('./pages/AdminPartners'));
const AdminMembershipVerification = lazy(() => import('./pages/AdminMembershipVerification'));
const Partners = lazy(() => import('./pages/Partners'));
const PartnerDetail = lazy(() => import('./pages/PartnerDetail'));
const PartnerConsultant = lazy(() => import('./pages/PartnerConsultant'));
const PartnerArticle = lazy(() => import('./pages/PartnerArticle'));
const AgencyDetail = lazy(() => import('./pages/AgencyDetail'));
const MassMarketing = lazy(() => import('./pages/MassMarketing'));
const ComplianceRCR = lazy(() => import('./pages/ComplianceRCR'));
const MyReports = lazy(() => import('./pages/MyReports'));
const MyBugReports = lazy(() => import('./pages/MyBugReports'));
const ReportBuilder = lazy(() => import('./pages/ReportBuilder'));
const BrochureBuilder = lazy(() => import('./pages/BrochureBuilder'));
const BrochurePreview = lazy(() => import('./pages/BrochurePreview'));
const LetterBuilder = lazy(() => import('./pages/LetterBuilder'));
const TenantPortal = lazy(() => import('./pages/TenantPortal'));
const ClientsList = lazy(() => import('./pages/ClientsList'));
const ClientProfile = lazy(() => import('./pages/ClientProfile'));
const Referrals = lazy(() => import('./pages/Referrals'));
const RentalApplication = lazy(() => import('./pages/RentalApplication'));
const RentalPipeline = lazy(() => import('./pages/RentalPipeline'));
const InspectionForm = lazy(() => import('./pages/InspectionForm'));
const RentalPayments = lazy(() => import('./pages/RentalPayments'));
const InvestorPortfolio = lazy(() => import('./pages/InvestorPortfolio'));
const PropertyManagerPortfolio = lazy(() => import('./pages/PropertyManagerPortfolio'));
const CRM = lazy(() => import('./pages/CRM'));
const Sales = lazy(() => import('./pages/Sales'));
const BranchScenarioPlanner = lazy(() => import('./pages/BranchScenarioPlanner'));
const CommissionEngine = lazy(() => import('./pages/CommissionEngine'));
const SellerCMAReport = lazy(() => import('./pages/SellerCMAReport'));
const ListingManagement = lazy(() => import('./pages/ListingManagement'));
const OfficeListings = lazy(() => import('./pages/OfficeListings'));
const MembershipDealRoom = lazy(() => import('./pages/MembershipDealRoom'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Prospecting = lazy(() => import('./pages/Prospecting'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const News = lazy(() => import('./pages/News'));
const Admin = lazy(() => import('./pages/Admin'));
const AdminDemo = lazy(() => import('./pages/AdminDemo'));
const AdminDemoWebsites = lazy(() => import('./pages/AdminDemoWebsites'));
const EnterpriseRegistration = lazy(() => import('./pages/EnterpriseRegistration'));
const EnterpriseDashboard = lazy(() => import('./pages/EnterpriseDashboard'));
const EnterpriseAgencies = lazy(() => import('./pages/EnterpriseAgencies'));
const EnterpriseInvites = lazy(() => import('./pages/EnterpriseInvites'));
const EnterprisePerformance = lazy(() => import('./pages/EnterprisePerformance'));
const EnterprisePortalSyndication = lazy(() => import('./pages/EnterprisePortalSyndication'));
const PartnerEnterpriseRegistration = lazy(() => import('./pages/PartnerEnterpriseRegistration'));
const PartnerEnterpriseDashboard = lazy(() => import('./pages/PartnerEnterpriseDashboard'));
const PartnerEnterpriseFranchises = lazy(() => import('./pages/PartnerEnterpriseFranchises'));
const PartnerEnterpriseInvites = lazy(() => import('./pages/PartnerEnterpriseInvites'));
const PartnerConsultantInviteRegistration = lazy(() => import('./pages/PartnerConsultantInviteRegistration'));
const OutstandOAuthCallback = lazy(() => import('./pages/OutstandOAuthCallback'));
const SocialConnectCallback = lazy(() => import('./pages/SocialConnectCallback'));
const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));
const Settings = lazy(() => import('./pages/Settings'));
const IpmAcademy = lazy(() => import('./pages/IpmAcademy'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const RegistrationSuccess = lazy(() => import('./pages/RegistrationSuccess'));

// Minimal loading fallback for lazy-loaded routes
const PageLoader = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LogoLoading message="Loading..." style={{ minHeight: '60vh' }} />
    </div>
);

// Roles that require a completed subscription (plan selected + payment where applicable) before using the app
const SUBSCRIPTION_REQUIRED_ROLES = ['buyer', 'seller', 'investor', 'agency', 'independent_agent', 'agent', 'partner'];
// When subscription is incomplete, restrict the user to Settings → Subscription
// (where they can complete or retry payment) and the auth/registration flows.
// Previously dashboard paths were also allowed here under the assumption that
// a "complete payment" banner would be shown in-app - but no such banner is
// rendered, so users with `pending_payment` could in practice skip paying and
// use the entire product. Audit ref 5.4.
const ALLOWED_WHEN_INCOMPLETE = ['/settings', '/signup', '/client-signup', '/agency-signup', '/independent-agent-signup', '/partner-signup', '/partner-enterprise-signup', '/enterprise-signup', '/agency-agent-invite', '/partner-consultant-invite', '/account-invite', '/set-password', '/registration/success', '/login', '/forgot-password', '/logout'];

/** Local-only: set in client/.env.development.local - pairs with server DEV_BYPASS_AUTH */
const REACT_DEV_BYPASS_AUTH = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true';

function isSubscriptionIncomplete(user) {
    if (!user) return false;
    const role = (user.role || '').toLowerCase();
    if (!SUBSCRIPTION_REQUIRED_ROLES.includes(role)) return false;
    // agency_agent: often no plan on user (agency pays); allow through
    if (role === 'agency_agent') return false;
    const hasStripe = !!user.stripeSubscriptionId;
    if (hasStripe) return false;
    const plan = (user.subscriptionPlan || '').trim();
    const status = (user.subscriptionStatus || '').toLowerCase();
    // Partners only gate when they started v5 paid membership and haven't paid
    if (role === 'partner') return status === 'pending_payment';
    // v5 free buyer / investor / tenant
    if (plan === 'Free' && status !== 'pending_payment') return false;
    const pendingPayment = status === 'pending_payment';
    if (!plan || plan === '-') return true;
    if (pendingPayment) return true;
    return false;
}

/** Legacy registration URLs redirect into the unified /signup wizard (except enterprise invite). */
function AgencySignupEntry() {
    const [params] = useSearchParams();
    if ((params.get('enterpriseInviteToken') || '').trim()) {
        return <AgencyRegistration />;
    }
    return <Navigate to="/signup" replace state={{ who: 'agency' }} />;
}
function ClientSignupRedirect() {
    return <Navigate to="/signup" replace state={{ who: 'buyer' }} />;
}
function IndependentAgentSignupRedirect() {
    return <Navigate to="/signup" replace state={{ who: 'agent' }} />;
}
function PartnerSignupRedirect() {
    const [params] = useSearchParams();
    if (portalForHostname() && params.get('partnerEnterpriseInviteToken')?.trim()) return <PartnerRegistration />;
    return <Navigate to="/signup" replace state={{ who: 'partner' }} />;
}
function EnterpriseSignupRedirect() {
    return <Navigate to="/signup" replace state={{ step: 'solutions' }} />;
}

/** Membership Network has no CRM or Vault pages - send them home (and open Add Lead if that was the intent). */
function BlockMembershipPage({ children, toDealRoom = false }) {
    const location = useLocation();
    const user = getStoredUser();
    const blocked = isMembershipExperience(user);
    useEffect(() => {
        if (!blocked) return;
        const wantAdd = new URLSearchParams(location.search).get('action') === 'addLead';
        if (wantAdd) {
            window.setTimeout(() => window.dispatchEvent(new CustomEvent(OPEN_ADD_LEAD_EVENT)), 0);
        }
    }, [blocked, location.search]);
    if (blocked) {
        if (toDealRoom) {
            const leadId = String(location.pathname.split('/clients/')[1] || '').split('/')[0];
            if (leadId) return <Navigate to={`/membership/deals/${leadId}`} replace />;
        }
        return <Navigate to={membershipAppHomePath(user)} replace />;
    }
    return children;
}

/** Admin “hide sidebar nav” flags also block the matching routes. */
function BlockHiddenNav({ navId, children }) {
    const user = getStoredUser();
    if (isNavHidden(user, navId)) return <Navigate to="/" replace />;
    return children;
}

/** Platform admin uses /admin/listings — not the agent listing book. */
function BlockAdminFromAgentListings({ children }) {
    if (isPlatformAdminUser(getStoredUser())) return <Navigate to="/admin/listings" replace />;
    return children;
}

// 1. Protected Route
const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const path = location.pathname || '';
    const [devBypassReady, setDevBypassReady] = useState(() => {
        if (!REACT_DEV_BYPASS_AUTH) return true;
        try {
            const u = JSON.parse(localStorage.getItem('user') || 'null');
            return !!(u && u.token === '__IPM_DEV_BYPASS__' && u._id);
        } catch {
            return false;
        }
    });
    const [devBypassError, setDevBypassError] = useState(null);

    useEffect(() => {
        if (!REACT_DEV_BYPASS_AUTH || devBypassReady) return undefined;
        let cancelled = false;
        fetch('/api/auth/dev-whoami')
            .then(async (r) => {
                const text = await r.text();
                let data = null;
                if (text) {
                    try {
                        data = JSON.parse(text);
                    } catch {
                        throw new Error(
                            `Non-JSON response (${r.status}). Is the API running on port 5001 and the client proxy correct? Body: ${text.slice(0, 120)}`
                        );
                    }
                }
                return { ok: r.ok, data, status: r.status };
            })
            .then(({ ok, data, status }) => {
                if (cancelled) return;
                if (!ok || !data || !data._id) {
                    throw new Error(
                        data?.message ||
                            (status === 404
                                ? 'Dev bypass disabled on API (add DEV_BYPASS_AUTH=true to .env and restart).'
                                : `Dev bypass failed (HTTP ${status})`)
                    );
                }
                localStorage.setItem('user', JSON.stringify(data));
                setDevBypassReady(true);
            })
            .catch((e) => {
                if (!cancelled) setDevBypassError(e.message || 'Dev bypass failed');
            });
        return () => {
            cancelled = true;
        };
    }, [devBypassReady]);

    if (REACT_DEV_BYPASS_AUTH && devBypassError) {
        return (
            <div style={{ padding: 40, maxWidth: 480, margin: '0 auto', fontFamily: 'system-ui' }}>
                <h1 style={{ fontSize: 20 }}>Dev bypass failed</h1>
                <p style={{ color: '#64748b' }}>{devBypassError}</p>
                <p style={{ fontSize: 14, color: '#94a3b8' }}>
                    Add <code>DEV_BYPASS_AUTH=true</code> to your API <code>.env</code>, restart the server, and ensure
                    MongoDB has at least one user with role <code>agency</code> (or set <code>DEV_BYPASS_AGENCY_ID</code>
                    ).
                </p>
            </div>
        );
    }

    if (REACT_DEV_BYPASS_AUTH && !devBypassReady) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <LogoLoading message="Dev bypass: loading test agency…" style={{ minHeight: '40vh' }} />
            </div>
        );
    }

    const userStr = localStorage.getItem('user');
    const user = userStr ? (() => { try { return JSON.parse(userStr); } catch (_) { return null; } })() : null;
    const loginPath = loginPathForSession(user);
    if (!user) return <Navigate to={loginPath} />;
    // Bypass the subscription gate for dev-bypass and for admins running a
    // demo session - demo accounts are not paying customers, so they
    // shouldn't get bounced into Settings → Subscription. Audit ref 5.4.
    const skipSubscriptionGate =
        (REACT_DEV_BYPASS_AUTH && user.token === '__IPM_DEV_BYPASS__') ||
        !!getDemoState();
    if (
        sessionRequiresPasswordChange(user) &&
        path !== '/set-password' &&
        path !== '/account-invite' &&
        path !== '/login'
    ) {
        return <Navigate to="/set-password" replace />;
    }
    if (needsTermsAcceptance(user) && path !== loginPath) {
        return <Navigate to={loginPath} replace />;
    }
    if (
        !skipSubscriptionGate &&
        isSubscriptionIncomplete(user) &&
        !ALLOWED_WHEN_INCOMPLETE.some((p) => path === p || path.startsWith(p + '?') || path.startsWith(p + '/'))
    ) {
        return <Navigate to="/settings?tab=subscription" replace />;
    }
    return children;
};

// 2. The Layout Component (Navbar + Footer)
const LayoutInner = ({ children }) => {
    const location = useLocation();
    const { NotificationContainer } = useNotification();
    const { t } = useTranslation();
    const { setMobileOpen } = useSidebar();
    
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [navSearchQuery, setNavSearchQuery] = useState('');
    const [leadSearchResults, setLeadSearchResults] = useState([]);
    const [leadSearchMessage, setLeadSearchMessage] = useState('');
    const [leadSearchSmartMatchError, setLeadSearchSmartMatchError] = useState('');
    const [leadSearchError, setLeadSearchError] = useState('');
    const [leadSearchLoading, setLeadSearchLoading] = useState(false);
    const [showLeadSearchPopup, setShowLeadSearchPopup] = useState(false);
    const [analyticsEnabled, setAnalyticsEnabled] = useState(() => hasAnalyticsConsent());
    const [sessionTick, setSessionTick] = useState(0);

    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || 'null');
        } catch (_) {
            return null;
        }
    }, [sessionTick]);

    const handleLogout = () => {
        window.location.href = logoutToLogin();
    };

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
        setShowUserMenu(false);
    }, [location.pathname]);

    useEffect(() => {
        const bump = () => setSessionTick((n) => n + 1);
        window.addEventListener(SESSION_UPDATED_EVENT, bump);
        window.addEventListener('storage', bump);
        return () => {
            window.removeEventListener(SESSION_UPDATED_EVENT, bump);
            window.removeEventListener('storage', bump);
        };
    }, []);

    useEffect(() => {
        const handleConsentChanged = (event) => {
            const nextValue = event?.detail?.value === 'accepted';
            setAnalyticsEnabled(nextValue);
        };
        window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChanged);
        return () => window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentChanged);
    }, []);
    const pathNorm = String(location.pathname || '').replace(/\/+$/, '') || '/';
    const hideNav = ['/login', '/tyson-engage/login', '/tyson-engage/forgot-password', '/signup', '/forgot-password', '/set-password', '/account-invite', '/agency-signup', '/enterprise-signup', '/partner-enterprise-signup', '/independent-agent-signup', '/agency-agent-invite', '/partner-consultant-invite', '/client-signup', '/partner-signup', '/social/connect/callback', '/signed'].includes(pathNorm)
        || pathNorm.startsWith('/client-form/');
    const showPublicLayout = !hideNav && !portalForHostname();
    const isAuthFlow = hideNav;

    // Dashboard-style pages: show solid top nav in flow (no overlay); sidebar starts below nav
    const isDashboardLayout = isDashboardLayoutRoute(location.pathname, { authenticated: !!user });

    // Warm the dashboard payload only inside the app shell - not on public
    // marketing pages, where it competes with hero video + landing JS.
    useEffect(() => {
        if (user?._id && isDashboardLayout) {
            prewarmDashboard(user._id);
            scheduleIdleRoutePrefetch();
        }
    }, [user?._id, isDashboardLayout]);

    useEffect(() => {
        if (!user?._id || !user?.token) return undefined;
        let cancelled = false;
        const signOutOfficeChange = () => {
            const loginPath = logoutToLogin({ fallback: '/login' });
            window.location.assign(`${loginPath}?session=office-changed`);
        };
        const checkSession = () => {
            api.get('/api/auth/session', { params: { _: Date.now() } })
                .then((res) => {
                    if (cancelled || !res.data?.ok) return;
                    const current = getStoredUser();
                    if (!current) return;
                    const isAgent = String(current.role || '').toLowerCase() === 'agency_agent';
                    const freshAgency = res.data.agencyId ? String(res.data.agencyId) : '';
                    const curAgency = current.agencyId ? String(current.agencyId) : '';
                    if (isAgent && freshAgency && curAgency && freshAgency !== curAgency) {
                        signOutOfficeChange();
                        return;
                    }
                    if (!isAgent) return;
                    const norm = (v) => String(v || '').trim();
                    const officeKeys = ['agencyId', 'agencyName', 'branchName', 'branchId'];
                    const officeDrift = officeKeys.some((key) => {
                        const fromServer = res.data[key];
                        if (fromServer == null || fromServer === '') return false;
                        return norm(fromServer) !== norm(current[key]);
                    });
                    if (officeDrift) {
                        persistUserSession({
                            ...current,
                            agencyId: res.data.agencyId ?? current.agencyId,
                            agencyName: res.data.agencyName ?? current.agencyName,
                            branchName: res.data.branchName ?? current.branchName,
                            branchId: res.data.branchId ?? current.branchId,
                        });
                    }
                })
                .catch((err) => {
                    if (cancelled) return;
                    const code = err?.response?.data?.code;
                    if (err?.response?.status === 401 && code === 'SESSION_REVOKED') {
                        signOutOfficeChange();
                    }
                });
        };
        checkSession();
        const onVisible = () => {
            if (document.visibilityState === 'visible') checkSession();
        };
        document.addEventListener('visibilitychange', onVisible);
        const interval = setInterval(checkSession, 45000);
        return () => {
            cancelled = true;
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user?._id, user?.token]);

    useEffect(() => {
        if (!user?._id || !isDashboardLayout) return undefined;
        let cancelled = false;
        api.get(`/api/users/${user._id}`, { params: { _: Date.now() } })
            .then((res) => {
                if (cancelled || !res.data?.featureAccess) return;
                const current = getStoredUser();
                if (!current) return;
                const prev = JSON.stringify(current.featureAccess || {});
                const next = JSON.stringify(res.data.featureAccess);
                if (prev === next) return;
                persistUserSession({ ...current, featureAccess: res.data.featureAccess });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [user?._id, isDashboardLayout]);

    // When demo mode is active AND we're on a public-layout route, the demo bar
    // sits at top:0 (no dashboard topbar to sit under). Add a class to <body>
    // so global CSS can push the public navbar / page content down by the bar's
    // ~36px height - otherwise the bar would overlap the public navbar.
    useEffect(() => {
        const inDemo = !!getDemoState();
        const cls = 'demo-public-offset';
        if (inDemo && !isDashboardLayout) document.body.classList.add(cls);
        else document.body.classList.remove(cls);
        return () => { document.body.classList.remove(cls); };
    }, [isDashboardLayout, location.pathname]);
    // Partners (list + detail) also embed their own HomeFigmaTopbar, like Services/Pricing.
    const isPartnersRoute = location.pathname === '/partners' || location.pathname.startsWith('/partners/');
    // Public agency view embeds its own HomeFigmaTopbar (dark) over a hero image.
    const isAgencyRoute = location.pathname.startsWith('/agencies/');
    // Public search embeds HomeFigmaTopbar the same way - never the logged-in dashboard nav.
    const isSearchRoute = location.pathname === '/search';
    // Home, Services, Pricing: same fixed landing bar + same layout shell as home/services (layout-home).
    const isLandingNav = ['/', '/our-services', '/pricing'].includes(location.pathname);
    const isLayoutHome = ['/', '/our-services', '/pricing'].includes(location.pathname) || isPartnersRoute || isAgencyRoute || isSearchRoute;
    /* Home rebuild: Home hero embeds the Figma top bar; Services and Pricing now
       render the same Figma top bar in dark-mode on their own pages. Hide the
       legacy global landing nav on all three routes to prevent a double bar.
       Partners, the agency view, and public search do the same. */
    const hideGlobalLandingNavForHome = ['/', '/our-services', '/pricing'].includes(location.pathname) || isPartnersRoute || isAgencyRoute || isSearchRoute;
    const navClass = showPublicLayout ? (isLandingNav ? 'news-nav news-nav--landing' : 'news-nav news-nav--hero') : 'news-nav news-nav--solid';
    const isAgentOrAgency = user && ['agency', 'agency_agent', 'independent_agent', 'agent'].includes(String(user.role).toLowerCase());
    const useAdashChrome = isDashboardLayout && isAdashChromeUser(user);

    useEffect(() => {
        if (useAdashChrome) {
            applyAdashRailMode(readAdashRailMode());
            return undefined;
        }
        document.documentElement.style.removeProperty('--adash-rail-w');
        document.documentElement.style.removeProperty('--adash-inset');
        delete document.documentElement.dataset.adashRail;
        return undefined;
    }, [useAdashChrome]);

    const handleLeadSearch = (e) => {
        e.preventDefault();
        const q = navSearchQuery.trim();
        if (!q || !user?._id) return;
        setLeadSearchLoading(true);
        setShowLeadSearchPopup(true);
        setLeadSearchResults([]);
        setLeadSearchMessage('');
        setLeadSearchError('');
        setLeadSearchSmartMatchError('');
        api.get('/api/properties/search-for-leads', { params: { q, userId: user._id, _: Date.now() } })
            .then((res) => {
                setLeadSearchResults(res.data.results || []);
                setLeadSearchMessage(res.data.message || '');
                setLeadSearchSmartMatchError(res.data.smartMatchError || '');
                setLeadSearchError('');
                setLeadSearchLoading(false);
            })
            .catch((err) => {
                const msg = err.response?.data?.error || err.message || 'Search failed. Please try again.';
                setLeadSearchResults([]);
                setLeadSearchMessage('');
                setLeadSearchSmartMatchError('');
                setLeadSearchError(msg);
                setLeadSearchLoading(false);
            });
    };

    return (
        <>
            <NotificationContainer />
            {pathNorm !== '/signed' && <CookieConsent />}
            {analyticsEnabled && (
                <Suspense fallback={null}>
                    <SpeedInsights />
                </Suspense>
            )}
            {/* Split dashboard top bar - all dashboard routes except agent/agency/enterprise
                screens, which use the new AdashAppBar chrome. White-label kits still theme it. */}
            {isDashboardLayout && user && !isAuthFlow && !useAdashChrome && (
                <Suspense fallback={null}>
                    <DashboardSplitTopBar
                        user={user}
                        searchValue={navSearchQuery}
                        onSearchChange={setNavSearchQuery}
                        onLeadSearchSubmit={handleLeadSearch}
                        isAgentOrAgency={!!isAgentOrAgency}
                    />
                </Suspense>
            )}
            {isDashboardLayout && user && !isAuthFlow && useAdashChrome && (
                <Suspense fallback={null}>
                    <AdashSideNav user={user} />
                    <AdashAppBar user={user} />
                </Suspense>
            )}
            {/* DemoModeBar is rendered on EVERY route (it self-hides when not in
                demo mode) so the admin can always see they're in demo mode and
                exit it - even from public-layout pages like /property/:id reached
                from a CommsPanel listing link. Positioning is route-aware:
                dashboard layouts have a 56px split topbar above the bar, public
                layouts don't, so the bar sits at top:0 and we push the page down
                via body class. */}
            <DemoModeBar topOffset={isDashboardLayout ? (useAdashChrome ? ADASH_BAR_HEIGHT : 56) : 0} />

            {/* --- NAVBAR (hidden for dashboard layouts - they use DashboardSplitTopBar) --- */}
            {showPublicLayout && !isDashboardLayout && !hideGlobalLandingNavForHome && (
                <nav className={navClass}>
                    {isLandingNav ? (
                        <div className="nav-landing-inner">
                            <div className="nav-landing-brand">
                                <Link to="/" className="nav-landing-logo-link">
                                    <BrandLogo className="nav-landing-logo-img" />
                                    <BrandWordmark className="logo-text-white" />
                                </Link>
                            </div>
                            <div className="nav-landing-center" aria-label="Main navigation">
                                <Link
                                    to="/"
                                    className={[
                                        'nav-landing-link',
                                        location.pathname === '/' ? 'nav-landing-link--active' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                    aria-current={location.pathname === '/' ? 'page' : undefined}
                                >
                                    {t('nav.home')}
                                </Link>
                                {user && (
                                    <Link to="/news" className="nav-landing-link">
                                        {t('nav.myIpmNews')}
                                    </Link>
                                )}
                            </div>
                            <div className="nav-landing-trailing">
                                {user && <NavSettingsDropdown />}
                                {user ? (
                                    <div
                                        className="user-dropdown-wrapper"
                                        style={{ position: 'relative' }}
                                        onMouseEnter={() => setShowUserMenu(true)}
                                        onMouseLeave={() => setShowUserMenu(false)}
                                    >
                                        <div className="user-profile-pill" role="button" aria-haspopup="true" aria-expanded={showUserMenu}>
                                            <div className="user-avatar-circle">
                                                {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
                                            </div>
                                            <span className="user-name-text">{user.name || 'User'}</span>
                                            <i className={`fas fa-chevron-down ${showUserMenu ? 'fa-rotate-180' : ''}`} style={{ fontSize: '12px', transition: '0.3s' }}></i>
                                        </div>
                                        {showUserMenu && (
                                            <div className="user-dropdown-menu">
                                                <Link
                                                    to={homePathForUser(user)}
                                                    className="dropdown-item"
                                                >
                                                    <i className="fas fa-columns"></i> {t('nav.dashboard')}
                                                </Link>
                                                <Link to="/settings" className="dropdown-item">
                                                    <i className="fas fa-cog"></i> {t('nav.settings')}
                                                </Link>
                                                <div className="dropdown-divider"></div>
                                                <button onClick={handleLogout} className="dropdown-item logout-item">
                                                    <i className="fas fa-sign-out-alt"></i> {t('nav.logout')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <Link to="/signup" className="nav-landing-btn-signup">
                                            {t('nav.signUp')}
                                        </Link>
                                        <Link to="/login" className="nav-landing-btn-myipm">
                                            myIPM <span aria-hidden>→</span>
                                        </Link>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="nav-mobile-toggle"
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                                >
                                    <i className={mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'} />
                                </button>
                            </div>
                        </div>
                    ) : (
                    <>
                    {/* LEFT: Hamburger (mobile, dashboard only) + Logo + IPM */}
                    <div className="nav-left" style={{ display: 'flex', alignItems: 'center' }}>
                        {isDashboardLayout && (
                            <button
                                type="button"
                                className="sidebar-hamburger"
                                onClick={() => setMobileOpen(true)}
                                aria-label="Open sidebar"
                            >
                                <i className="fas fa-bars" />
                            </button>
                        )}
                        <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                            <>
                                <BrandLogo className="nav-logo-img" />
                                <BrandWordmark className="logo-text-white" />
                            </>
                        </Link>
                    </div>

                    {/* CENTER (logged in only, never on landing page): Search bar (active for agents/agencies) + icons */}
                    {user && !isLandingNav && (
                        <div className="nav-center-logged" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: '1', maxWidth: '560px', margin: '0 24px', justifyContent: 'center' }}>
                            {isAgentOrAgency ? (
                                <form onSubmit={handleLeadSearch} style={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: '400px', height: '40px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.12)', padding: '0 14px', gap: '10px' }}>
                                    <span style={{ width: 32, height: 32, borderRadius: '50%', background: '#0d9488', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <i className="fas fa-search" style={{ color: 'white', fontSize: 12 }} />
                                    </span>
                                    <input
                                        type="text"
                                        value={navSearchQuery}
                                        onChange={(e) => setNavSearchQuery(e.target.value)}
                                        placeholder="Find properties for your leads"
                                        aria-label="Search properties for your leads"
                                        style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', color: '#fff', fontSize: 14, outline: 'none' }}
                                    />
                                </form>
                            ) : (
                                <div style={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: '400px', height: '40px', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.1)', padding: '0 14px', opacity: 0.85, pointerEvents: 'none', cursor: 'default' }}>
                                    <span style={{ width: 32, height: 32, borderRadius: '50%', background: '#0d9488', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginRight: 10, flexShrink: 0 }}>
                                        <i className="fas fa-search" style={{ color: 'white', fontSize: 12 }} />
                                    </span>
                                    <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>Describe your ideal next investment</span>
                                </div>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                                <span className="nav-icon-circle nav-icon-circle--teal" title="Voice search" aria-hidden><i className="fas fa-microphone" /></span>
                                <span className="nav-icon-circle nav-icon-circle--orange" title="Map" aria-hidden><i className="fas fa-map" /></span>
                                <span className="nav-icon-circle nav-icon-circle--muted" title="Notifications" aria-hidden><i className="fas fa-bell" /></span>
                                <span className="nav-icon-circle nav-icon-circle--muted" title="Messages" aria-hidden><i className="far fa-envelope" /></span>
                            </div>
                        </div>
                    )}

                    {/* RIGHT: Desktop actions + Mobile hamburger */}
                    <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        {user && <NavSettingsDropdown />}
                        {!isLandingNav && user && (
                            <Link to="/news" className="btn-outline" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="far fa-newspaper"></i> {t('nav.myIpmNews')}
                            </Link>
                        )}

                        <Link to="/new-developments" className="btn-outline" style={{ display: 'none', textDecoration: 'none', alignItems: 'center', gap: '8px' }}>
                            <i className="far fa-building"></i> {t('nav.newProperties')}
                        </Link>

                        {/* USER PROFILE DROPDOWN (desktop): hover to open, leave to close */}
                        {user ? (
                            <div
                                className="user-dropdown-wrapper"
                                style={{ position: 'relative' }}
                                onMouseEnter={() => setShowUserMenu(true)}
                                onMouseLeave={() => setShowUserMenu(false)}
                            >
                                <div className="user-profile-pill" role="button" aria-haspopup="true" aria-expanded={showUserMenu}>
                                    <div className="user-avatar-circle">
                                        {user.name && user.name.charAt ? user.name.charAt(0).toUpperCase() : 'U'}
                                    </div>
                                    <span className="user-name-text">{user.name || 'User'}</span>
                                    <i className={`fas fa-chevron-down ${showUserMenu ? 'fa-rotate-180' : ''}`} style={{ fontSize: '12px', transition: '0.3s' }}></i>
                                </div>

                                {showUserMenu && (
                                    <div className="user-dropdown-menu">
                                        <Link
                                            to={homePathForUser(user)}
                                            className="dropdown-item"
                                        >
                                            <i className="fas fa-columns"></i> {t('nav.dashboard')}
                                        </Link>
                                        <Link to="/settings" className="dropdown-item">
                                            <i className="fas fa-cog"></i> {t('nav.settings')}
                                        </Link>
                                        <div className="dropdown-divider"></div>
                                        <button onClick={handleLogout} className="dropdown-item logout-item">
                                            <i className="fas fa-sign-out-alt"></i> {t('nav.logout')}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <Link to="/login" className="btn-outline" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <i className="fas fa-sign-in-alt"></i> {t('nav.logIn')}
                                </Link>
                                <Link to="/signup" className="btn-filled" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    {t('nav.signUp')}
                                </Link>
                            </>
                        )}

                        {/* Mobile hamburger toggle (shown via CSS on small screens) */}
                        <button
                            type="button"
                            className="nav-mobile-toggle"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                        >
                            <i className={mobileMenuOpen ? 'fas fa-times' : 'fas fa-bars'} />
                        </button>
                    </div>
                    </>
                    )}
                </nav>
            )}

            {/* Lead search results popup (agents/agencies) */}
            {showLeadSearchPopup && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Property search results"
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '80px', padding: '80px 16px 24px' }}
                    onClick={() => setShowLeadSearchPopup(false)}
                >
                    <div
                        style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', maxWidth: '520px', width: '100%', maxHeight: 'calc(100vh - 120px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#11575C' }}>Properties for your search</h3>
                            <button type="button" onClick={() => setShowLeadSearchPopup(false)} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#64748b', cursor: 'pointer', padding: '4px 8px' }} aria-label="Close"><i className="fas fa-times" /></button>
                        </div>
                        <div style={{ padding: '16px 24px', overflowY: 'auto', flex: 1 }}>
                            {leadSearchLoading ? (
                                <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}><i className="fas fa-circle-notch fa-spin" style={{ marginRight: 8 }} /> Searching your listings…</p>
                            ) : leadSearchResults.length === 0 ? (
                                <>
                                    {leadSearchError && (
                                        <p style={{ margin: '0 0 12px 0', padding: '10px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 13, color: '#b91c1c' }}>{leadSearchError}</p>
                                    )}
                                    {!leadSearchError && <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>No matching properties. Try different keywords.</p>}
                                </>
                            ) : (
                                <>
                                    {leadSearchSmartMatchError && (
                                        <p style={{ margin: '0 0 12px 0', padding: '12px 14px', background: '#fef2f2', borderRadius: 8, fontSize: 13, color: '#b91c1c', border: '1px solid #fecaca' }}>
                                            <strong>Smart matching failed - error:</strong><br />
                                            <span style={{ wordBreak: 'break-word' }}>{leadSearchSmartMatchError}</span>
                                        </p>
                                    )}
                                    {leadSearchError && !leadSearchSmartMatchError && (
                                        <p style={{ margin: '0 0 12px 0', padding: '10px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 13, color: '#b91c1c' }}>{leadSearchError}</p>
                                    )}
                                    {leadSearchMessage && (
                                        <p style={{ margin: '0 0 12px 0', padding: '8px 12px', background: '#f0fdfa', borderRadius: 8, fontSize: 12, color: '#0f766e' }}>{leadSearchMessage}</p>
                                    )}
                                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                                    {leadSearchResults.map((r, i) => {
                                        const p = r.property || r;
                                        const score = typeof r.score === 'number' ? r.score : null;
                                        const reason = (r.reason && String(r.reason).trim()) || null;
                                        return (
                                            <li key={p._id || i} style={{ marginBottom: '12px', padding: '12px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14, marginBottom: 4 }}>{buildListingLabel(p)}</div>
                                                        <div style={{ fontSize: 12, color: '#64748b' }}>{p.location || p.price || ''}</div>
                                                        {reason && <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, fontStyle: 'italic' }}>{reason}</div>}
                                                    </div>
                                                    <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700, color: '#11575C', background: 'rgba(17,87,92,0.12)', padding: '4px 8px', borderRadius: '8px' }}>{score != null ? `${score}% match` : '-'}</span>
                                                </div>
                                                <Link to={{ pathname: `/property/${p._id}`, state: propertyNavStateFromLocation(location, 'Home', { fromSearch: true, searchMatch: { score: score ?? undefined, reason: reason ?? undefined } }) }} style={{ display: 'inline-block', marginTop: 8, fontSize: 13, fontWeight: 600, color: '#11575C', textDecoration: 'none' }} onClick={() => setShowLeadSearchPopup(false)}>View property →</Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile nav menu overlay (public pages, not dashboard); home uses in-hero drawer */}
            {showPublicLayout && !isDashboardLayout && !hideGlobalLandingNavForHome && (
                <div className={`nav-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
                    {user && <NavSettingsDropdown />}
                    {user ? (
                        <>
                            <Link to={homePathForUser(user)}>
                                <i className="fas fa-columns"></i> {t('nav.dashboard')}
                            </Link>
                            <Link to="/settings">
                                <i className="fas fa-cog"></i> {t('nav.settings')}
                            </Link>
                            <Link to="/">
                                <i className="fas fa-home"></i> {t('nav.home')}
                            </Link>
                            <Link to="/news">
                                <i className="far fa-newspaper"></i> {t('nav.myIpmNews')}
                            </Link>
                            <div className="mobile-menu-divider" />
                            <button onClick={handleLogout}>
                                <i className="fas fa-sign-out-alt"></i> {t('nav.logout')}
                            </button>
                        </>
                    ) : (
                        <>
                            <Link to="/">
                                <i className="fas fa-home"></i> {t('nav.home')}
                            </Link>
                            <Link to="/login">
                                <i className="fas fa-sign-in-alt"></i> {t('nav.logIn')}
                            </Link>
                            <Link to="/signup">
                                <i className="fas fa-user-plus"></i> {t('nav.signUp')}
                            </Link>
                        </>
                    )}
                </div>
            )}

            {/* --- PAGE CONTENT (wrapper for dashboard layout so sidebar sits below nav) --- */}
            <div className={[
                'layout-content',
                isDashboardLayout ? 'layout-dashboard-shell' : '',
                isDashboardLayout && getDemoState() ? 'layout-demo-active' : '',
                useAdashChrome ? 'layout-adash-chrome' : '',
                showPublicLayout && !isDashboardLayout ? 'layout-with-nav' : '',
                isLayoutHome ? 'layout-home' : '',
            ].filter(Boolean).join(' ')}>
                {children}
            </div>

            {isDashboardLayout && (
                <Suspense fallback={null}>
                    <GuidedTour />
                    {user && (isMembershipAgencyOrAgent(user) || isMembershipPartner(user) || ['partner', 'partner_agent'].includes(String(user.role || '').toLowerCase())) && <ExpertModals user={user} />}
                    {user && <FloatingBugsFeedback variant="global" />}
                    {user && <BugReviewBanner />}
                    {user && <BugFixReviewModal userId={user._id || user.id} />}
                    {user && <ForcedBugReviewPopup userId={user._id || user.id} user={user} />}
                    {user && <EnquiryDetailModal />}
                    <PayPropDrawer />
                </Suspense>
            )}
            {user && (
                <Suspense fallback={null}>
                    <MembershipVerificationResultModal />
                    <MembershipVerificationReviewHost />
                </Suspense>
            )}

            {!isAuthFlow && user && (
                <Suspense fallback={null}>
                    <MobileFAB />
                    <FloatingChat hideFloatingButton={isDashboardLayout} />
                </Suspense>
            )}
        </>
    );
};

const Layout = ({ children }) => {
    const location = useLocation();
    let isAuthenticated = false;
    try {
        isAuthenticated = !!localStorage.getItem('user');
    } catch (_) { /* SSR / privacy mode */ }
    const isDashboardLayout = isDashboardLayoutRoute(location.pathname, { authenticated: isAuthenticated });
    const content = <LayoutInner>{children}</LayoutInner>;
    return isDashboardLayout ? <SidebarProvider>{content}</SidebarProvider> : content;
};

function App() {
  // Sync navigations. The default transition queue lets background
  // updates (message polling) hold a nav click until a full reload.
  return (
    <Router unstable_useTransitions={false}>
        <RuntimeIdentityBoundary runtime={<Suspense fallback={<PageLoader />}><PortalPublicBoundary><ProtectedRoute><AdminRuntimeIdentity /></ProtectedRoute></PortalPublicBoundary></Suspense>}>
        <div className="ipm-app-shell">
        <WorkbenchPreviewSiteBanner />
        <PropdataImportProvider>
        <LightstoneRetrievalProvider>
        <CmaInfoRetrievalProvider>
        <Layout>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
            <PortalPublicBoundary>
            <Routes>
                {/* Public Routes */}
                <Route path="/" element={<PortalRoot><Home /></PortalRoot>} />
                <Route path="/profile/:id" element={<Profile />} />
                <Route path="/login" element={<Login />} />
                <Route path="/tyson-engage/login" element={<PortalAuthAlias><Login experience="tyson-engage" /></PortalAuthAlias>} />
                <Route path="/account-invite" element={<AcceptAccountInvite />} />
                <Route path="/set-password" element={<SetPassword />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/tyson-engage/forgot-password" element={<PortalAuthAlias recovery><ForgotPassword experience="tyson-engage" /></PortalAuthAlias>} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/ipm-academy" element={<ProtectedRoute><IpmAcademy /></ProtectedRoute>} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/collection" element={<Collection />} />
                <Route path="/search" element={<SearchResults />} />
                {/* Shared marketing dashboard - every role with a Marketing tab
                    (agency, agency_agent, independent_agent, agent, partner)
                    now lands on the same enterprise-style marketing page. The
                    role-aware <Sidebar /> inside keeps each user's own nav. */}
                <Route path="/marketing" element={<ProtectedRoute><BlockHiddenNav navId="marketing"><EnterpriseDashboard activeTab="marketing" /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/mass-marketing" element={<ProtectedRoute><MassMarketing /></ProtectedRoute>} />
                <Route path="/outstand/oauth-callback" element={<ProtectedRoute><OutstandOAuthCallback /></ProtectedRoute>} />
                <Route path="/social/connect/callback" element={<ProtectedRoute><SocialConnectCallback /></ProtectedRoute>} />
                <Route path="/new-developments" element={<NewDevelopments />} />
                <Route path="/partners" element={<Partners />} />
                <Route path="/partners/:id/consultants/:consultantId/:kind/:itemId" element={<PartnerArticle />} />
                <Route path="/partners/:id/consultants/:consultantId" element={<PartnerConsultant />} />
                <Route path="/partners/:id/:kind/:itemId" element={<PartnerArticle />} />
                <Route path="/partners/:id" element={<PartnerDetail />} />
                <Route path="/agencies/:id" element={<AgencyDetail />} />
                <Route path="/templates/preview/*" element={<TemplatePreviewGallery />} />
                <Route path="/about" element={<About />} />
                {/* Services & Pricing pages are hidden site-wide - any direct
                    visit (incl. old bookmarks/deep links) redirects to Home. */}
                <Route path="/our-services" element={<Navigate to="/" replace />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/add-listing" element={<BlockAdminFromAgentListings><AddListing /></BlockAdminFromAgentListings>} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/pricing" element={<Navigate to="/" replace />} />
                <Route path="/news/:id" element={<NewsDetail />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/help" element={<HelpCenter />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/client-signup" element={<ClientSignupRedirect />} />
                {/* Public POPIA consent page - reached via a tokenised link in the
                    consent-request email; no login required. */}
                <Route path="/signed" element={<SignatureComplete />} />
                <Route path="/consent/:token" element={<ConsentPage />} />
                {/* Public viewing slot picker - tokenised link in booking email; no login. */}
                <Route path="/viewing/:token" element={<ViewingBookingPage />} />
                <Route path="/meet/:token" element={<MeetBookingPage />} />
                {/* Public client tracker (build spec Screen 9) - reached via a
                    tokenised link in the congratulations email; no login required. */}
                <Route path="/track/:token" element={<ClientTracker />} />
                <Route path="/client-form/:token" element={<ClientFormFill />} />
                <Route path="/registration/success" element={<RegistrationSuccess />} />
                <Route path="/vault" element={<ProtectedRoute><BlockMembershipPage><Vault /></BlockMembershipPage></ProtectedRoute>} />
                <Route path="/rental-dashboard" element={<ProtectedRoute><BlockHiddenNav navId="rentals"><RentalDashboard /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/rental-applications" element={<ProtectedRoute><BlockHiddenNav navId="rentals"><RentalDashboard initialTab="applications" /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/rental-pipeline" element={<ProtectedRoute><BlockHiddenNav navId="rentals"><RentalPipeline /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/inspections/new" element={<ProtectedRoute><BlockHiddenNav navId="rentals"><InspectionForm /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/rental-payments" element={<ProtectedRoute><BlockHiddenNav navId="rentals"><RentalPayments /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/rental-application" element={<RentalApplication />} />
                <Route path="/rental-maintenance" element={<ProtectedRoute><BlockHiddenNav navId="rentals"><RentalMaintenance /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
                <Route path="/tenant-portal" element={<ProtectedRoute><TenantPortal /></ProtectedRoute>} />
                <Route path="/clients" element={<ProtectedRoute><BlockMembershipPage><ClientsList /></BlockMembershipPage></ProtectedRoute>} />
                <Route path="/clients/:leadId" element={<ProtectedRoute><BlockMembershipPage toDealRoom><ClientProfile /></BlockMembershipPage></ProtectedRoute>} />
                <Route path="/referrals" element={<ProtectedRoute><BlockHiddenNav navId="referrals"><Referrals /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/investor-portfolio" element={<ProtectedRoute><InvestorPortfolio /></ProtectedRoute>} />
                <Route path="/managed-portfolio" element={<ProtectedRoute><BlockHiddenNav navId="rentals"><PropertyManagerPortfolio /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
                <Route path="/agents" element={<ProtectedRoute><Agents /></ProtectedRoute>} />
                <Route path="/crm" element={<ProtectedRoute><BlockHiddenNav navId="crm"><BlockMembershipPage><CRM /></BlockMembershipPage></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/crm/cma-report" element={<ProtectedRoute><BlockMembershipPage><SellerCMAReport /></BlockMembershipPage></ProtectedRoute>} />
                <Route path="/sales" element={<ProtectedRoute><BlockHiddenNav navId="revenue"><Sales /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/sales/deals" element={<ProtectedRoute><BlockHiddenNav navId="revenue"><Sales /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/branch-planner" element={<ProtectedRoute><BlockHiddenNav navId="branch-planner"><BranchScenarioPlanner /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/commission-engine" element={<ProtectedRoute><CommissionEngine /></ProtectedRoute>} />
                <Route path="/agency-signup" element={<AgencySignupEntry />} />
                <Route path="/enterprise-signup" element={<EnterpriseSignupRedirect />} />
                <Route path="/partner-enterprise-signup" element={<PartnerEnterpriseRegistration />} />
                <Route path="/partner-enterprise-dashboard" element={<ProtectedRoute><PartnerEnterpriseDashboard /></ProtectedRoute>} />
                <Route path="/partner-enterprise/franchises" element={<ProtectedRoute><PartnerEnterpriseFranchises /></ProtectedRoute>} />
                <Route path="/partner-enterprise/invites" element={<PartnerEnterpriseInvites />} />
                <Route path="/partner-consultant-invite" element={<PartnerConsultantInviteRegistration />} />

                {/* Enterprise routes */}
                <Route path="/enterprise-dashboard" element={<ProtectedRoute><EnterpriseDashboard activeTab="dashboard" /></ProtectedRoute>} />
                <Route path="/enterprise/performance-country" element={<ProtectedRoute><EnterpriseDashboard activeTab="country" /></ProtectedRoute>} />
                <Route path="/enterprise/performance-franchise" element={<ProtectedRoute><EnterpriseDashboard activeTab="franchise" /></ProtectedRoute>} />
                <Route path="/enterprise/performance-branch" element={<ProtectedRoute><EnterpriseDashboard activeTab="branch" /></ProtectedRoute>} />
                <Route path="/enterprise/royalty-engine" element={<ProtectedRoute><EnterpriseDashboard activeTab="royalty" /></ProtectedRoute>} />
                <Route path="/enterprise/compliance-report" element={<ProtectedRoute><EnterpriseDashboard activeTab="compliance" /></ProtectedRoute>} />
                <Route path="/enterprise/portal-syndication" element={<ProtectedRoute><EnterpriseDashboard activeTab="portal" /></ProtectedRoute>} />
                <Route path="/enterprise/marketing" element={<ProtectedRoute><EnterpriseDashboard activeTab="marketing" /></ProtectedRoute>} />
                <Route path="/enterprise/vault" element={<ProtectedRoute><EnterpriseDashboard activeTab="vault" /></ProtectedRoute>} />
                <Route path="/enterprise/agencies" element={<ProtectedRoute><EnterpriseAgencies /></ProtectedRoute>} />
                {/* Public when ?token=XXX is in the URL (invitee landing /
                    auto-accept). The component itself swaps to the
                    Sidebar-wrapped pending-invites list once the user is
                    logged in and there's no token in the URL. */}
                <Route path="/enterprise/invites" element={<EnterpriseInvites />} />
                <Route path="/enterprise/performance" element={<ProtectedRoute><EnterprisePerformance /></ProtectedRoute>} />
                <Route path="/enterprise/syndication" element={<ProtectedRoute><EnterprisePortalSyndication /></ProtectedRoute>} />
                {/* Agency-scoped Portal Syndication - same UI, no /enterprise prefix.
                    Lets agency / agency_agent / independent_agent users manage portal
                    connections (Property24, Private Property, etc.). */}
                <Route path="/portal-syndication" element={<ProtectedRoute><EnterprisePortalSyndication /></ProtectedRoute>} />

                {/* Unified partner dashboard.
                    ONE component for every partner type - bond_originator,
                    conveyancer, bank, short_term_insurance, life_insurance,
                    valuer. The legacy BondOriginatorDashboard and
                    ConveyancerDashboard pages were retired here; their URLs
                    keep working by mounting PartnerDashboard with the
                    matching activeTab. */}
                <Route path="/partner-dashboard"          element={<ProtectedRoute><PartnerDashboard activeTab="dashboard" /></ProtectedRoute>} />
                <Route path="/partner/pipeline"           element={<ProtectedRoute><PartnerDashboard activeTab="pipeline" /></ProtectedRoute>} />
                <Route path="/partner/crm"                element={<ProtectedRoute><PartnerDashboard activeTab="crm" /></ProtectedRoute>} />
                <Route path="/partner/referrals"          element={<ProtectedRoute><PartnerDashboard activeTab="referrals" /></ProtectedRoute>} />
                <Route path="/partner/partners"           element={<ProtectedRoute><PartnerDashboard activeTab="partners" /></ProtectedRoute>} />
                <Route path="/partner/vault"              element={<ProtectedRoute><PartnerDashboard activeTab="vault" /></ProtectedRoute>} />

                <Route path="/conveyancer"                element={<ProtectedRoute><PartnerDashboard activeTab="dashboard" /></ProtectedRoute>} />
                <Route path="/conveyancer/pipeline"       element={<ProtectedRoute><PartnerDashboard activeTab="pipeline" /></ProtectedRoute>} />
                <Route path="/conveyancer/crm"            element={<ProtectedRoute><PartnerDashboard activeTab="crm" /></ProtectedRoute>} />
                <Route path="/conveyancer/referrals"      element={<ProtectedRoute><PartnerDashboard activeTab="referrals" /></ProtectedRoute>} />
                <Route path="/conveyancer/partners"       element={<ProtectedRoute><PartnerDashboard activeTab="partners" /></ProtectedRoute>} />
                <Route path="/conveyancer/vault"          element={<ProtectedRoute><PartnerDashboard activeTab="vault" /></ProtectedRoute>} />
                <Route path="/conveyancer/*"              element={<ProtectedRoute><PartnerDashboard activeTab="dashboard" /></ProtectedRoute>} />

                <Route path="/bond-originator-dashboard"  element={<ProtectedRoute><PartnerDashboard activeTab="dashboard" /></ProtectedRoute>} />
                <Route path="/bond-originator"            element={<ProtectedRoute><PartnerDashboard activeTab="dashboard" /></ProtectedRoute>} />
                <Route path="/bond-originator/pipeline"   element={<ProtectedRoute><PartnerDashboard activeTab="pipeline" /></ProtectedRoute>} />
                <Route path="/bond-originator/crm"        element={<ProtectedRoute><PartnerDashboard activeTab="crm" /></ProtectedRoute>} />
                <Route path="/bond-originator/referrals"  element={<ProtectedRoute><PartnerDashboard activeTab="referrals" /></ProtectedRoute>} />
                <Route path="/bond-originator/partners"   element={<ProtectedRoute><PartnerDashboard activeTab="partners" /></ProtectedRoute>} />
                <Route path="/bond-originator/vault"      element={<ProtectedRoute><PartnerDashboard activeTab="vault" /></ProtectedRoute>} />
                <Route path="/bond-originator/*"          element={<ProtectedRoute><PartnerDashboard activeTab="dashboard" /></ProtectedRoute>} />

                {/* Legacy agency dashboard URL redirects to the main dashboard route below. */}
                <Route path="/agency-dashboard" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
                <Route path='/agent-dashboard' element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/listing-management" element={<ProtectedRoute><BlockAdminFromAgentListings><BlockHiddenNav navId="listings"><ListingManagement /></BlockHiddenNav></BlockAdminFromAgentListings></ProtectedRoute>} />
                <Route path="/office-listings" element={<ProtectedRoute><BlockAdminFromAgentListings><BlockHiddenNav navId="listings">{seesOfficeListingsTab(getStoredUser()) ? <OfficeListings /> : <Navigate to="/listing-management" replace />}</BlockHiddenNav></BlockAdminFromAgentListings></ProtectedRoute>} />
                <Route path="/membership/deals/:leadId" element={<ProtectedRoute><MembershipDealRoom /></ProtectedRoute>} />
                <Route path="/inventory" element={<ProtectedRoute>{isInventoryHidden(getStoredUser()) ? <Navigate to="/listing-management" replace /> : <Inventory />}</ProtectedRoute>} />
                <Route path="/prospecting" element={<ProtectedRoute>{isProspectingHidden(getStoredUser()) ? <Navigate to="/listing-management" replace /> : <Prospecting />}</ProtectedRoute>} />
                <Route path="/portfolio" element={<ProtectedRoute><Portfolio /></ProtectedRoute>} />
                <Route path="/property/:id" element={<Property />} />
                <Route path="/admin/demo" element={<ProtectedRoute><AdminDemo /></ProtectedRoute>} />
                <Route path="/admin/demo-websites" element={<ProtectedRoute><AdminDemoWebsites /></ProtectedRoute>} />
                <Route path="/admin/marketing" element={<ProtectedRoute><EnterpriseDashboard activeTab="marketing" /></ProtectedRoute>} />
                <Route path="/admin/accounts" element={<ProtectedRoute><Admin activeTab="accounts" /></ProtectedRoute>} />
                <Route path="/admin/demo-accounts" element={<ProtectedRoute><Admin activeTab="demo-accounts" /></ProtectedRoute>} />
                <Route path="/admin/listings" element={<ProtectedRoute><Admin activeTab="listings" /></ProtectedRoute>} />
                <Route path="/admin/communication-billing" element={<ProtectedRoute><AdminCommunicationBilling /></ProtectedRoute>} />
                <Route path="/admin/email-health" element={<ProtectedRoute><AdminEmailHealth /></ProtectedRoute>} />
                <Route path="/admin/screening" element={<ProtectedRoute><AdminScreening /></ProtectedRoute>} />
                <Route path="/admin/referrals" element={<ProtectedRoute><AdminReferrals /></ProtectedRoute>} />
                <Route path="/admin/partners" element={<ProtectedRoute><AdminPartners /></ProtectedRoute>} />
                <Route path="/admin/membership-verification" element={<ProtectedRoute><AdminMembershipVerification /></ProtectedRoute>} />
                <Route path="/admin/mass-marketing" element={<ProtectedRoute><AdminMassMarketing /></ProtectedRoute>} />
                <Route path="/admin/ipm-sales" element={<ProtectedRoute><IpmSales /></ProtectedRoute>} />
                <Route path="/admin/template-portal" element={<ProtectedRoute><AdminTemplatePortal /></ProtectedRoute>} />
                <Route path="/compliance/fica" element={<ProtectedRoute><FicaQueue /></ProtectedRoute>} />
                <Route path="/compliance/rcr" element={<ProtectedRoute><BlockHiddenNav navId="compliance-rcr"><ComplianceRCR /></BlockHiddenNav></ProtectedRoute>} />
                <Route path="/my-bug-reports" element={<ProtectedRoute><MyBugReports /></ProtectedRoute>} />
                <Route path="/my-reports" element={<ProtectedRoute>{isStudioHidden(getStoredUser()) || isNavHidden(getStoredUser(), 'my-reports') ? <Navigate to="/" replace /> : <MyReports />}</ProtectedRoute>} />
                <Route path="/my-reports/:id" element={<ProtectedRoute><StudioTabRoute tab="reports"><ReportBuilder /></StudioTabRoute></ProtectedRoute>} />
                <Route path="/brochure-preview" element={<ProtectedRoute><StudioTabRoute tab="brochures"><BrochurePreview /></StudioTabRoute></ProtectedRoute>} />
                <Route path="/brochures/:id" element={<ProtectedRoute><StudioTabRoute tab="brochures"><BrochureBuilder /></StudioTabRoute></ProtectedRoute>} />
                <Route path="/letters/:id" element={<ProtectedRoute><StudioTabRoute tab="documents"><LetterBuilder /></StudioTabRoute></ProtectedRoute>} />
                <Route path="/admin/bugs" element={<ProtectedRoute><Admin activeTab="bugs" /></ProtectedRoute>} />
                <Route path="/admin/sprints" element={<ProtectedRoute><IpmSprintPlanner /></ProtectedRoute>} />
                <Route path="/admin/workbench" element={<Navigate to="/admin" replace />} />
                <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/demo-builder" element={<ProtectedRoute><AdminDemoBuilder /></ProtectedRoute>} />
                <Route path="/admin/cost-model" element={<ProtectedRoute><AdminUnitEconomics /></ProtectedRoute>} />
                <Route path="/admin/scheduler" element={<ProtectedRoute><AdminScheduler /></ProtectedRoute>} />
                <Route path="/admin/ops" element={<ProtectedRoute><AdminOpsHealth /></ProtectedRoute>} />
                <Route path="/independent-agent-signup" element={<IndependentAgentSignupRedirect />} />
                <Route path="/agency-agent-invite" element={<AgencyAgentInviteRegistration />} />
                <Route path="/partner-signup" element={<PartnerSignupRedirect />} />

                {/* Private Routes */}
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/my-ads" element={<ProtectedRoute><MyAds /></ProtectedRoute>} />
                <Route path="/saved" element={<ProtectedRoute><Saved /></ProtectedRoute>} />
                <Route path="*" element={<PortalPublicNotFound />} />
            </Routes>
            </PortalPublicBoundary>
            </Suspense>
        </Layout>
        </CmaInfoRetrievalProvider>
        </LightstoneRetrievalProvider>
        </PropdataImportProvider>
        </div>
        </RuntimeIdentityBoundary>
    </Router>
  );
}

export default App;
